import React, { useState, useCallback } from 'react';
import styled, { createGlobalStyle, ThemeProvider } from 'styled-components';
import Sidebar from './Sidebar';
import MobileHeader from './MobileHeader';
import MobileNavigation from './MobileNavigation';
import { theme } from '../theme';
import useResponsive from '../hooks/useResponsive';

// Usaremos fuentes de Google a través de un link en el HTML o CSS directamente

interface LayoutProps {
  children: React.ReactNode;
}

// Estilos globales para toda la aplicación
const GlobalStyle = createGlobalStyle`
  /* Google Fonts import eliminado. Añade el <link> en public/index.html */
  
  * {
    box-sizing: border-box;
  }
  
  body {
    margin: 0;
    padding: 0;
    font-family: 'Inter', 'Rubik', Arial, Helvetica, sans-serif;
    background-color: ${theme.colors.background.default};
    color: ${theme.colors.text.primary};
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  
  h1, h2, h3, h4, h5, h6 {
    font-weight: ${theme.typography.fontWeights.bold};
    margin-bottom: ${theme.spacing.md};
  }
  
  h1 {
    font-size: ${theme.typography.sizes.h1};
  }
  
  h2 {
    font-size: ${theme.typography.sizes.h2};
  }
  
  h3 {
    font-size: ${theme.typography.sizes.h3};
  }
  
  h4 {
    font-size: ${theme.typography.sizes.h4};
  }
  
  h5 {
    font-size: ${theme.typography.sizes.h5};
  }
  
  h6 {
    font-size: ${theme.typography.sizes.h6};
  }
  
  p {
    margin-bottom: ${theme.spacing.md};
  }
  
  a {
    color: ${theme.colors.primary.main};
    text-decoration: none;
    transition: ${theme.transitions.fast};
    
    &:hover {
      color: ${theme.colors.primary.light};
    }
  }
  
  button {
    font-family: 'Inter', 'Rubik', Arial, Helvetica, sans-serif;
  }
  
  input, select, textarea {
    font-family: 'Inter', 'Rubik', Arial, Helvetica, sans-serif;
  }
  
  // Estilos para scrollbar personalizados
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  ::-webkit-scrollbar-track {
    background: ${theme.colors.background.default};
  }
  
  ::-webkit-scrollbar-thumb {
    background: ${theme.colors.text.disabled};
    border-radius: 4px;
  }
  
  ::-webkit-scrollbar-thumb:hover {
    background: ${theme.colors.text.secondary};
  }
`;

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${theme.colors.background.default};
`;

const SidebarContainer = styled.div<{ $isMobile: boolean }>`
  display: ${props => props.$isMobile ? 'none' : 'block'};
`;

const MainContent = styled.main<{ $isMobile: boolean }>`
  flex: 1;
  padding: ${props => props.$isMobile ? `${theme.spacing.lg}` : '50px'};
  padding-top: ${props => props.$isMobile ? '80px' : '50px'};
  overflow: auto;
  margin: 0px;
  position: relative;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    margin: ${theme.spacing.sm};
    padding: ${theme.spacing.lg};
    padding-top: 80px;
    border-radius: ${theme.borderRadius.medium};
  }
`;

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isMobile } = useResponsive();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  console.log('Layout - isMobile:', isMobile);

  const handleMobileMenuToggle = useCallback(() => {
  console.log('[Layout] handleMobileMenuToggle. isMobileMenuOpen:', !isMobileMenuOpen);
  console.log('Mobile menu toggle clicked');
  setIsMobileMenuOpen(prev => !prev);
}, [isMobileMenuOpen]);

  const handleMobileMenuClose = useCallback(() => {
  console.log('[Layout] handleMobileMenuClose. isMobileMenuOpen: false');
  console.log('Mobile menu close');
  setIsMobileMenuOpen(false);
}, []);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <LayoutContainer>
        {isMobile && (
          <>
            <MobileHeader onMenuToggle={handleMobileMenuToggle} />
            <MobileNavigation 
              isOpen={isMobileMenuOpen} 
              onClose={handleMobileMenuClose} 
            />
          </>
        )}
        
        <SidebarContainer $isMobile={isMobile}>
          <Sidebar />
        </SidebarContainer>
        
        <MainContent $isMobile={isMobile}>
          {children}
        </MainContent>
      </LayoutContainer>
    </ThemeProvider>
  );
};

export default Layout;
